/**
 * Persist generation images + manifests to Firebase.
 * Storage: projects/{projectId}/generations/{id}.png
 *          projects/{projectId}/refs/{refId}.png (approved)
 * Firestore: projects/{projectId}/generations/{id}
 */
import { collection, deleteDoc, doc, getDocs, setDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadString, uploadBytes } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { writesStayLocal } from './localOnly';
import { storagePathForExport, storagePathForGeneration } from './picturePaths';

function requireAuth() {
  const user = auth.currentUser;
  if (!user) {
    const err = new Error('Sign in required to sync generations to Firebase');
    err.code = 'AUTH_REQUIRED';
    throw err;
  }
  return user;
}

function stripBase64(generation) {
  if (!generation?.output) return generation;
  const { base64, ...output } = generation.output;
  return { ...generation, output };
}

/** Upload generation image + metadata after a successful OpenAI generate. */
export async function persistGenerationToCloud(projectId, generation) {
  if (writesStayLocal()) return { ...generation, cloud: false, skipped: true, reason: 'LOCAL' };
  requireAuth();
  if (!generation?.id) return null;

  const storagePath = `projects/${projectId}/generations/${generation.id}.png`;
  let downloadPath = storagePath;

  if (generation.output?.base64) {
    await uploadString(ref(storage, storagePath), generation.output.base64, 'base64', {
      contentType: generation.output.mimeType || 'image/png',
      customMetadata: {
        projectId,
        generationId: generation.id,
        workDpi: String(generation.resolution?.workDpi || 72),
        exportDpi: String(generation.resolution?.exportDpi || 300),
      },
    });
  } else if (generation.output?.contentUrl || generation.output?.path) {
    const url = generation.output.contentUrl || `/content/projects/${projectId}/${generation.output.path}`;
    const res = await fetch(url);
    if (res.ok) {
      const buf = await res.arrayBuffer();
      await uploadBytes(ref(storage, storagePath), buf, {
        contentType: generation.output.mimeType || 'image/png',
      });
    }
  }

  const meta = {
    ...stripBase64(generation),
    projectId,
    storagePath: downloadPath,
    syncedAt: new Date().toISOString(),
    syncedBy: auth.currentUser.email || null,
    source: 'firebase',
  };

  await setDoc(doc(db, 'projects', projectId, 'generations', generation.id), meta, { merge: true });

  // Lightweight project shell so structure/generate continuity has a Firestore anchor.
  await setDoc(
    doc(db, 'projects', projectId),
    {
      id: projectId,
      updatedAt: new Date().toISOString(),
      lastGenerationId: generation.id,
      lastGenerationAt: generation.createdAt || new Date().toISOString(),
    },
    { merge: true }
  );

  return { ...generation, storagePath: downloadPath, cloud: true };
}

export async function persistApprovedReferenceToCloud(projectId, { reference, generation }) {
  if (writesStayLocal()) return { skipped: true, reason: 'LOCAL' };
  requireAuth();
  if (!reference?.id) return null;

  const storagePath = `projects/${projectId}/refs/${reference.id}.png`;
  const localUrl =
    generation?.output?.contentUrl ||
    (generation?.output?.path
      ? `/content/projects/${projectId}/${generation.output.path}`
      : reference?.path
        ? `/content/projects/${projectId}/${reference.path}`
        : null);

  if (localUrl) {
    const res = await fetch(localUrl);
    if (res.ok) {
      const buf = await res.arrayBuffer();
      const contentType = res.headers.get('content-type') || 'image/png';
      await uploadBytes(ref(storage, storagePath), buf, { contentType });
    }
  }

  await setDoc(
    doc(db, 'projects', projectId, 'refs', reference.id),
    {
      ...reference,
      projectId,
      storagePath,
      fromGenerationId: generation?.id || null,
      approved: true,
      lockedAsReference: true,
      syncedAt: new Date().toISOString(),
    },
    { merge: true }
  );

  if (generation?.id) {
    await setDoc(
      doc(db, 'projects', projectId, 'generations', generation.id),
      {
        approved: true,
        lockedAsReference: true,
        approvedReferenceId: reference.id,
        approvedAt: new Date().toISOString(),
      },
      { merge: true }
    );
  }

  return { reference, storagePath };
}

/** Attach a Storage URL for the picture and any already-produced 300dpi file. */
export async function attachGenerationPicture(projectId, generation) {
  if (!generation) return generation;
  const storagePath = storagePathForGeneration(projectId, generation);
  let imageUrl = '';
  if (storagePath) {
    try {
      imageUrl = await getDownloadURL(ref(storage, storagePath));
    } catch {
      imageUrl = '';
    }
  }
  const output = { ...(generation.output || {}) };
  delete output.base64;
  if (imageUrl) output.imageUrl = imageUrl;
  if (!output.path && generation.id) output.path = `generations/${generation.id}.png`;

  let export300 = generation.export300 || null;
  if (export300 && (export300.path || export300.contentUrl || export300.storagePath)) {
    const exportPath = storagePathForExport(projectId, export300);
    let downloadUrl = '';
    if (exportPath) {
      try {
        downloadUrl = await getDownloadURL(ref(storage, exportPath));
      } catch {
        downloadUrl = '';
      }
    }
    export300 = {
      ...export300,
      storagePath: exportPath || export300.storagePath || '',
      downloadUrl,
    };
  }

  return {
    ...generation,
    storagePath: storagePath || generation.storagePath || '',
    output,
    export300,
  };
}

/** Pictures already saved for this book. Docs only. Storage URLs wait until a row opens. */
export async function listCloudGenerations(projectId) {
  requireAuth();
  const snap = await getDocs(collection(db, 'projects', projectId, 'generations'));
  const generations = snap.docs.map((entry) => {
    const data = entry.data() || {};
    const generation = { ...data, id: data.id || entry.id };
    const output = { ...(generation.output || {}) };
    delete output.base64;
    const storagePath = storagePathForGeneration(projectId, { ...generation, output });
    return {
      ...generation,
      output,
      storagePath: generation.storagePath || storagePath || '',
    };
  });
  generations.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  return generations;
}

/** Best-effort remove of a generation (+ optional locked ref) from Firebase. */
export async function deleteCloudReference(projectId, approvedReferenceId) {
  if (writesStayLocal()) return { skipped: true, reason: 'LOCAL' };
  requireAuth();
  if (!approvedReferenceId) return null;
  try {
    await deleteObject(ref(storage, `projects/${projectId}/refs/${approvedReferenceId}.png`));
  } catch (e) {
    if (e?.code !== 'storage/object-not-found') throw e;
  }
  try {
    await deleteDoc(doc(db, 'projects', projectId, 'refs', approvedReferenceId));
  } catch (e) {
    if (e?.code !== 'not-found') throw e;
  }
  return { deleted: true, id: approvedReferenceId, projectId, source: 'firebase' };
}

export async function deleteCloudGeneration(
  projectId,
  generationId,
  { approvedReferenceId } = {}
) {
  if (writesStayLocal()) return { skipped: true, reason: 'LOCAL' };
  requireAuth();
  if (!generationId) return null;

  try {
    await deleteObject(ref(storage, `projects/${projectId}/generations/${generationId}.png`));
  } catch (e) {
    if (e?.code !== 'storage/object-not-found') throw e;
  }

  try {
    await deleteDoc(doc(db, 'projects', projectId, 'generations', generationId));
  } catch (e) {
    // Missing Firestore doc is fine — local was source of truth for some attempts.
    if (e?.code !== 'not-found') throw e;
  }

  if (approvedReferenceId) {
    await deleteCloudReference(projectId, approvedReferenceId);
  }

  return { deleted: true, id: generationId, projectId, source: 'firebase' };
}

/** Upload an already-approved local bible reference (e.g. Era 1 concept sheet) to Storage + Firestore. */
export async function syncLocalApprovedReference(projectId, reference) {
  if (writesStayLocal()) return { skipped: true, reason: 'LOCAL' };
  if (!reference?.id || !reference?.approved) return null;
  if (!auth.currentUser) return { skipped: true, reason: 'AUTH_REQUIRED' };
  // Skip repeat uploads of large concept sheets in the same browser session.
  const skipKey = `ref-synced:${projectId}:${reference.id}`;
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(skipKey)) {
      return { skipped: true, reason: 'ALREADY_SYNCED' };
    }
  } catch {
    /* private mode */
  }
  const result = await persistApprovedReferenceToCloud(projectId, { reference, generation: null });
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(skipKey, '1');
  } catch {
    /* ignore */
  }
  return result;
}

/** Persist eras/structure shell so Firebase mirrors Book → Eras → Scenes → Pages. */
export async function syncProjectStructureShell(projectId, project, structure) {
  if (writesStayLocal()) return { skipped: true, reason: 'LOCAL' };
  if (!auth.currentUser || !projectId) return null;
  const eras = structure?.eras?.length
    ? structure.eras
    : (structure?.chapters || []).filter((c) => c.kind === 'era');
  await setDoc(
    doc(db, 'projects', projectId),
    {
      id: projectId,
      title: project?.title || projectId,
      organizationModel: project?.organizationModel || null,
      sectionOrder: project?.sectionOrder || null,
      bibleVersion: project?.bibleVersion || null,
      eraIds: eras.map((e) => e.id),
      erasSummary: eras.map((e) => ({
        id: e.id,
        year: e.year,
        title: e.title,
        castRoles: (e.cast || []).map((c) => c.role),
        sceneCount: (e.scenes || []).length,
        pageRange: e.pageRange || null,
        referenceIds: e.referenceIds || [],
      })),
      updatedAt: new Date().toISOString(),
      syncedBy: auth.currentUser.email || null,
    },
    { merge: true }
  );
  for (const era of eras) {
    await setDoc(
      doc(db, 'projects', projectId, 'eras', era.id),
      {
        ...era,
        projectId,
        syncedAt: new Date().toISOString(),
      },
      { merge: true }
    );
  }
  return { eraCount: eras.length };
}
